import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, Link2, Plug, RefreshCw, Send, Store, UploadCloud, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY } from "../../lib/format";

const inputCls = "h-10 px-3 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";

function CredentialModal({ marketplace, onClose, onSaved }) {
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);
  const m = marketplace;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/marketplaces/${m.slug}/credentials`, { credentials: creds });
      toast.success(`${m.name} API bilgileri kaydedildi`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[85vh] overflow-y-auto"
        data-testid={`mp-cred-modal-${m.slug}`}>
        <p className="font-display font-bold text-white text-lg flex items-center gap-2"><KeyRound size={16} className="text-blue-400" /> {m.name} — API Bilgileri</p>
        <p className="text-[11px] text-neutral-500">Bilgiler AES-256 ile şifrelenerek saklanır. Boş bırakılan alanlar değişmez.</p>
        {(m.field_schema || []).map((f) => {
          const existing = m.credentials?.[f.key];
          return (
            <div key={f.key}>
              <label className="text-[11px] text-neutral-500 block mb-1">{f.label}{f.required === false && " (opsiyonel)"}</label>
              <input type={f.secret ? "password" : "text"} value={creds[f.key] ?? ""}
                placeholder={existing ? `Mevcut: ${existing}` : ""}
                onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                className={`${inputCls} w-full`} data-testid={`mp-cred-${m.slug}-${f.key}`} autoComplete="new-password" />
            </div>
          );
        })}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving} className="h-10 px-5 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50" data-testid={`mp-cred-save-${m.slug}`}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-400">Vazgeç</button>
        </div>
      </form>
    </div>
  );
}

const STATUS_STYLE = {
  AKTIF: "bg-emerald-500/15 text-emerald-400",
  BEKLEMEDE: "bg-amber-500/15 text-amber-400",
  HATALI: "bg-rose-500/15 text-rose-400",
  YOK: "bg-neutral-500/15 text-neutral-500",
};
const STATUS_LABEL = { AKTIF: "AKTİF", BEKLEMEDE: "BEKLEMEDE", HATALI: "HATALI", YOK: "—" };

function PriceRuleEditor({ marketplace, onSaved }) {
  const [pct, setPct] = useState(marketplace.price_adjust_percent ?? 0);
  const [saving, setSaving] = useState(false);
  const dirty = Number(pct) !== Number(marketplace.price_adjust_percent ?? 0);
  return (
    <div className="flex items-center gap-2" data-testid={`mp-price-rule-${marketplace.slug}`}>
      <span className="text-[11px] text-neutral-500">Fiyat kuralı:</span>
      <div className="flex items-center bg-[#0F1115] border border-[#272B33] rounded-lg overflow-hidden">
        <input type="number" step="1" value={pct} onChange={(e) => setPct(e.target.value)}
          className="w-16 h-7 px-2 bg-transparent text-xs text-white focus:outline-none text-right" />
        <span className="text-[11px] text-neutral-500 pr-2">%</span>
      </div>
      {dirty && (
        <button onClick={async () => {
          setSaving(true);
          try {
            await api.put(`/admin/marketplaces/${marketplace.slug}/account`, { active: marketplace.active, price_adjust_percent: Number(pct) });
            toast.success(`${marketplace.name} fiyat kuralı: %${pct}`);
            onSaved();
          } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
        }} disabled={saving}
          className="h-7 px-2.5 rounded-lg bg-blue-600 text-white text-[11px] font-semibold disabled:opacity-50"
          data-testid={`mp-price-rule-save-${marketplace.slug}`}>
          {saving ? "..." : "Kaydet"}
        </button>
      )}
      {Number(pct) !== 0 && !dirty && (
        <span className="text-[10px] text-amber-400">ana fiyata %{pct} uygulanır</span>
      )}
    </div>
  );
}

export default function MarketplacePage() {
  const [tab, setTab] = useState("urunler");
  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [logs, setLogs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [channelPrices, setChannelPrices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mapForm, setMapForm] = useState(null);
  const [priceForm, setPriceForm] = useState(null);
  const [syncing, setSyncing] = useState("");
  const [credModal, setCredModal] = useState(null);
  const [testingMp, setTestingMp] = useState("");

  const testMp = async (m) => {
    setTestingMp(m.slug);
    try {
      const { data } = await api.post(`/admin/marketplaces/${m.slug}/test`, {}, { timeout: 60000 });
      data.ok ? toast.success(data.message) : toast.error(data.message);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setTestingMp(""); }
  };

  const load = () => {
    api.get("/admin/marketplaces").then(({ data }) => setAccounts(data)).catch(() => {});
    api.get("/admin/marketplaces/products").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/admin/marketplaces/queue").then(({ data }) => setQueue(data)).catch(() => {});
    api.get("/admin/marketplaces/logs").then(({ data }) => setLogs(data)).catch(() => {});
    api.get("/admin/marketplaces/mappings").then(({ data }) => setMappings(data)).catch(() => {});
    api.get("/admin/marketplaces/channel-prices").then(({ data }) => setChannelPrices(data)).catch(() => {});
  };
  useEffect(load, []);

  const doSync = async (productId, marketplace, action) => {
    setSyncing(`${productId}-${marketplace}-${action}`);
    try {
      const res = await api.post("/admin/marketplaces/sync", { product_id: productId, marketplace, action });
      res.data.ok ? toast.success(`${marketplace}: ${action} senkronu tamamlandı`) : toast.error(res.data.error || "Senkron hatası");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(""); }
  };

  const saveMapping = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/marketplaces/mappings", mapForm);
      toast.success("Eşleştirme kaydedildi"); setMapForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const savePrice = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/marketplaces/channel-prices", { ...priceForm, price: parseFloat(priceForm.price), sale_price: priceForm.sale_price ? parseFloat(priceForm.sale_price) : null });
      toast.success("Kanal fiyatı kaydedildi"); setPriceForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const TABS = [["urunler", "Ürün Kanal Durumu"], ["fiyatlar", "Kanal Fiyatları"], ["eslestirme", "Eşleştirmeler"], ["kuyruk", "Senkron Kuyruğu"], ["loglar", "Senkron Logları"]];
  const products = overview?.products || [];
  // Hesap kartları/alan şemaları zengin listeden (field_schema, maskeli kimlikler, son test);
  // overview yalnızca ürün kanal durumu için kullanılır
  const mps = accounts.length ? accounts : (overview?.marketplaces || []);
  const priceOf = (pid, ch) => channelPrices.find((c) => c.product_id === pid && c.channel === ch);

  return (
    <div className="space-y-5" data-testid="admin-marketplace">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Marketplace Merkezi</h1>
        <div className="flex gap-2 bg-[#181B20] border border-[#272B33] rounded-xl p-1 flex-wrap">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors ${tab === id ? "bg-blue-600 text-white" : "text-neutral-400"}`}
              data-testid={`mp-tab-${id}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* Hesap kartları — Pazaryeri Durumu */}
      <div className="grid sm:grid-cols-2 gap-4">
        {mps.map((m) => (
          <div key={m.slug} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 space-y-3" data-testid={`mp-account-${m.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display font-bold text-white flex items-center gap-2"><Store size={16} className="text-blue-400" /> {m.name}
                  {m.configured
                    ? <span className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full">✓ Bağlı</span>
                    : <span className="text-[10px] font-bold bg-neutral-500/15 text-neutral-500 px-2 py-0.5 rounded-full">○ Bağlı değil</span>}
                </p>
                <p className="text-[11px] text-neutral-500 mt-1">{m.api_version} · Komisyon %{m.commission_rate} · Mod: <span className="text-amber-400 font-mono-ed">{m.mode}</span></p>
                <div className="mt-1.5"><PriceRuleEditor marketplace={m} onSaved={load} /></div>
                {m.last_test && (
                  <p className={`text-[11px] mt-1 flex items-center gap-1 ${m.last_test.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid={`mp-last-test-${m.slug}`}>
                    {m.last_test.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                    {m.last_test.ok ? "Bağlantı başarılı" : "Bağlantı başarısız"} · Son kontrol: {fmtDate(m.last_test.at)}
                  </p>
                )}
              </div>
              <button onClick={async () => { await api.put(`/admin/marketplaces/${m.slug}/account`, { active: !m.active }); load(); }}
                className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${m.active ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-500/15 text-neutral-400"}`}
                data-testid={`mp-toggle-${m.slug}`}>
                {m.active ? "Aktif" : "Pasif"}
              </button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setCredModal(m)}
                className="h-9 px-3.5 rounded-xl border border-[#272B33] text-xs text-neutral-300 hover:border-blue-500 flex items-center gap-1.5"
                data-testid={`mp-creds-${m.slug}`}><KeyRound size={13} /> API Bilgileri</button>
              <button onClick={() => testMp(m)} disabled={testingMp === m.slug}
                className="h-9 px-3.5 rounded-xl border border-[#272B33] text-xs text-neutral-300 hover:border-blue-500 flex items-center gap-1.5 disabled:opacity-50"
                data-testid={`mp-test-${m.slug}`}>
                <Plug size={13} className={testingMp === m.slug ? "animate-pulse" : ""} /> {testingMp === m.slug ? "Test ediliyor..." : "Bağlantıyı Test Et"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {credModal && <CredentialModal marketplace={credModal} onClose={() => setCredModal(null)} onSaved={() => { setCredModal(null); load(); }} />}

      {tab === "urunler" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="mp-products-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
                <th className="px-5 py-3.5">Ürün</th>
                {mps.map((m) => <th key={m.slug} className="px-4 py-3.5">{m.name}</th>)}
                <th className="px-4 py-3.5 text-right">Manuel Senkron</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.product_id} className="border-b border-[#272B33]/50" data-testid={`mp-product-row-${p.sku || p.product_id}`}>
                  <td className="px-5 py-3">
                    <p className="text-white font-medium">{p.name}</p>
                    <p className="text-[11px] text-neutral-500 font-mono-ed">{p.sku} · stok {p.stock} (güvenlik: {p.safety_stock || 0})</p>
                  </td>
                  {mps.map((m) => {
                    const ch = p.channels?.[m.slug] || { status: "YOK" };
                    return (
                      <td key={m.slug} className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[ch.status]}`} data-testid={`mp-status-${m.slug}-${p.sku}`}>
                          {STATUS_LABEL[ch.status]}
                        </span>
                        {ch.last_sync && <p className="text-[10px] text-neutral-600 mt-1">{fmtDate(ch.last_sync)}</p>}
                        {ch.last_error && <p className="text-[10px] text-rose-400 mt-1 max-w-[180px] truncate" title={ch.last_error}>{ch.last_error}</p>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {mps.map((m) => (
                        <div key={m.slug} className="flex gap-1">
                          {[["product", Send, "Ürünü gönder"], ["stock", UploadCloud, "Stok gönder"], ["price", RefreshCw, "Fiyat gönder"]].map(([action, Icon, title]) => (
                            <button key={action} title={`${m.name}: ${title}`}
                              disabled={syncing === `${p.product_id}-${m.slug}-${action}`}
                              onClick={() => doSync(p.product_id, m.slug, action)}
                              className="p-2 rounded-lg text-neutral-500 hover:text-blue-400 hover:bg-blue-500/10 disabled:opacity-40"
                              data-testid={`mp-sync-${m.slug}-${action}-${p.sku}`}>
                              <Icon size={14} />
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Ürün yok</p>}
        </div>
      )}

      {tab === "fiyatlar" && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setPriceForm({ product_id: products[0]?.product_id || "", channel: "TRENDYOL", price: "", sale_price: "" })}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="channel-price-new">+ Kanal Fiyatı</button>
          </div>
          {priceForm && (
            <form onSubmit={savePrice} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 grid sm:grid-cols-5 gap-3 items-end" data-testid="channel-price-form">
              <select value={priceForm.product_id} onChange={(e) => setPriceForm({ ...priceForm, product_id: e.target.value })} className={inputCls} data-testid="cp-product">
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
              <select value={priceForm.channel} onChange={(e) => setPriceForm({ ...priceForm, channel: e.target.value })} className={inputCls} data-testid="cp-channel">
                {mps.map((m) => <option key={m.slug} value={m.slug.toUpperCase()}>{m.name}</option>)}
              </select>
              <input required type="number" step="0.01" placeholder="Liste fiyatı ₺" value={priceForm.price} onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} className={inputCls} data-testid="cp-price" />
              <input type="number" step="0.01" placeholder="İndirimli ₺ (ops.)" value={priceForm.sale_price} onChange={(e) => setPriceForm({ ...priceForm, sale_price: e.target.value })} className={inputCls} data-testid="cp-sale" />
              <button type="submit" className="h-10 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="cp-save">Kaydet</button>
            </form>
          )}
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
            {channelPrices.map((c) => {
              const p = products.find((x) => x.product_id === c.product_id);
              return (
                <div key={c.channel_price_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50" data-testid={`cp-row-${c.product_id}-${c.channel}`}>
                  <div>
                    <p className="text-sm text-white">{p?.name || c.product_id}</p>
                    <p className="text-[11px] text-neutral-500">{c.channel}</p>
                  </div>
                  <p className="text-sm text-white font-semibold">{fmtTRY(c.price)}{c.sale_price ? <span className="text-emerald-400 ml-2">{fmtTRY(c.sale_price)}</span> : null}</p>
                </div>
              );
            })}
            {channelPrices.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Kanal fiyatı tanımlı değil — varsayılan olarak Bigeldi fiyatı kullanılır</p>}
          </div>
        </>
      )}

      {tab === "eslestirme" && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setMapForm({ product_id: products[0]?.product_id || "", variant_id: "", marketplace: "trendyol", marketplace_product_id: "", marketplace_sku: "", marketplace_barcode: "" })}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2" data-testid="mapping-new">
              <Link2 size={15} /> Yeni Eşleştirme
            </button>
          </div>
          {mapForm && (
            <form onSubmit={saveMapping} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 grid sm:grid-cols-3 gap-3 items-end" data-testid="mapping-form">
              <select value={mapForm.product_id} onChange={(e) => setMapForm({ ...mapForm, product_id: e.target.value })} className={inputCls} data-testid="map-product">
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
              <select value={mapForm.marketplace} onChange={(e) => setMapForm({ ...mapForm, marketplace: e.target.value })} className={inputCls} data-testid="map-marketplace">
                {mps.map((m) => <option key={m.slug} value={m.slug}>{m.name}</option>)}
              </select>
              <input placeholder="Marketplace Ürün ID" value={mapForm.marketplace_product_id} onChange={(e) => setMapForm({ ...mapForm, marketplace_product_id: e.target.value })} className={inputCls} data-testid="map-mp-id" />
              <input placeholder="Marketplace SKU" value={mapForm.marketplace_sku} onChange={(e) => setMapForm({ ...mapForm, marketplace_sku: e.target.value })} className={inputCls} />
              <input placeholder="Marketplace Barkod" value={mapForm.marketplace_barcode} onChange={(e) => setMapForm({ ...mapForm, marketplace_barcode: e.target.value })} className={inputCls} />
              <button type="submit" className="h-10 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="map-save">Kaydet</button>
            </form>
          )}
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead><tr className="text-left text-neutral-500 border-b border-[#272B33]">
                <th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Bigeldi Ürün</th><th className="px-4 py-3">MP Ürün ID</th><th className="px-4 py-3">MP SKU</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3">Son Senkron</th>
              </tr></thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.mapping_id} className="border-b border-[#272B33]/40" data-testid={`mapping-row-${m.mapping_id}`}>
                    <td className="px-4 py-2.5 text-white capitalize">{m.marketplace}</td>
                    <td className="px-4 py-2.5 text-neutral-400">{products.find((p) => p.product_id === m.product_id)?.name || m.product_id}</td>
                    <td className="px-4 py-2.5 font-mono-ed text-neutral-300">{m.marketplace_product_id || "—"}</td>
                    <td className="px-4 py-2.5 font-mono-ed text-neutral-300">{m.marketplace_sku || "—"}</td>
                    <td className="px-4 py-2.5"><span className={`text-[10px] font-bold px-2 py-1 rounded-full ${STATUS_STYLE[m.sync_status || "YOK"]}`}>{STATUS_LABEL[m.sync_status] || m.sync_status}</span></td>
                    <td className="px-4 py-2.5 text-neutral-500">{m.last_sync ? fmtDate(m.last_sync) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mappings.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Eşleştirme yok</p>}
          </div>
        </>
      )}

      {tab === "kuyruk" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden" data-testid="sync-queue">
          {queue.map((j) => (
            <div key={j.job_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50 last:border-0" data-testid={`queue-row-${j.job_id}`}>
              <div>
                <p className="text-sm text-white capitalize">{j.marketplace} · {j.action}</p>
                <p className="text-[11px] text-neutral-500 font-mono-ed">{products.find((p) => p.product_id === j.product_id)?.name || j.product_id} · deneme: {j.attempts}</p>
                {j.last_error && <p className="text-[11px] text-rose-400 mt-0.5">{j.last_error}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${j.status === "done" ? "bg-emerald-500/15 text-emerald-400" : j.status === "failed" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>
                  {j.status === "done" ? "TAMAM" : j.status === "failed" ? "BAŞARISIZ" : "BEKLEMEDE"}
                </span>
                {j.status !== "done" && (
                  <button onClick={async () => { const r = await api.post(`/admin/marketplaces/queue/${j.job_id}/retry`); r.data.ok ? toast.success("Yeniden gönderildi") : toast.error(r.data.error || "Hata"); load(); }}
                    className="p-2 rounded-lg text-neutral-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid={`queue-retry-${j.job_id}`} title="Tekrar gönder">
                    <RefreshCw size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {queue.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Kuyruk boş</p>}
        </div>
      )}

      {tab === "loglar" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden" data-testid="sync-logs">
          {logs.map((l) => (
            <div key={l.log_id} className="flex items-center justify-between px-5 py-3 border-b border-[#272B33]/50 last:border-0 text-xs">
              <div>
                <p className="text-white capitalize">{l.marketplace} · {l.action}</p>
                <p className="text-neutral-500 mt-0.5">{l.message}</p>
              </div>
              <div className="text-right">
                <span className={`font-bold ${l.ok ? "text-emerald-400" : "text-rose-400"}`}>{l.ok ? "OK" : "HATA"}</span>
                <p className="text-neutral-600">{fmtDate(l.created_at)}</p>
              </div>
            </div>
          ))}
          {logs.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Log yok</p>}
        </div>
      )}
    </div>
  );
}
